import { parse, subWeeks, subYears } from 'date-fns';
import { useQuery } from 'urql';

import { useUserTrialDetails } from '@revelio/auth';
import {
  DATE_FORMAT_WITH_DAY,
  STANDARD_DATE_FORMAT,
  swapDateFormat,
} from '@revelio/core';
import { MonthWeekIndexQuery, TrialType } from '@revelio/data-access';

const lagDashboardDateConditional = (date: string, dateFormat: string) => {
  const parsedDate = parse(date, dateFormat, new Date());
  const laggedDate =
    dateFormat === DATE_FORMAT_WITH_DAY
      ? subWeeks(parsedDate, 53)
      : subYears(parsedDate, 1);
  try {
    return swapDateFormat(laggedDate, dateFormat);
  } catch (error) {
    console.error(error);
    return date;
  }
};

/**
 * Provides standardized date range filters for API calls, adjusting dates based on user trial status.
 * For trial users with a one-year data lag, the end dates are shifted back one year.
 * Returns two date range objects: one based on the latest month and another based on the latest week.
 */
export const useMonthApiFilters = () => {
  const { isTrialUser, trialType } = useUserTrialDetails();
  const [{ data: monthWeekIndexData }] = useQuery({
    query: MonthWeekIndexQuery,
    pause: isTrialUser === null,
  });

  if (
    !monthWeekIndexData?.index?.endMonth ||
    !monthWeekIndexData?.index.maxWeek
  ) {
    return null;
  }

  const hasTrialDataLag = trialType === TrialType.ONE_YEAR_DATA_LAG;
  const lastMonth = hasTrialDataLag
    ? lagDashboardDateConditional(
        monthWeekIndexData.index.endMonth,
        STANDARD_DATE_FORMAT
      )
    : monthWeekIndexData?.index?.endMonth;
  const lastStartDate = hasTrialDataLag
    ? lagDashboardDateConditional(
        monthWeekIndexData.index.maxWeek,
        DATE_FORMAT_WITH_DAY
      )
    : monthWeekIndexData?.index?.maxWeek;
  return {
    date_range_api_filters: {
      start_date: lagDashboardDateConditional(lastMonth, STANDARD_DATE_FORMAT),
      end_date: lastMonth,
    },
    date_range_full_api_filters: {
      start_date: lagDashboardDateConditional(
        lastStartDate,
        DATE_FORMAT_WITH_DAY
      ),
      end_date: lastStartDate,
    },
  };
};
